import { onLocaleChange, t } from "../i18n/index.js";
import { el } from "./dom.js";
import { diyaLamp, vigilBackdrop } from "./vigil.js";

/** Stages, in the order they complete. The bar reflects real work, not a timer. */
const STAGES = ["ui", "engine", "venue", "ready"] as const;
export type LoadStage = (typeof STAGES)[number];

/**
 * Shown until the venue can actually be walked around.
 *
 * The bar moves on real milestones rather than a fake timer, because on a
 * ₹10k phone over 4G the slow part is genuinely variable and a bar that lies
 * is worse than no bar.
 *
 * It is also the first thing anybody sees of the evening, which the first
 * version did not treat as worth anything: a system-font line and an emoji on
 * a flat field. It now lights the same lamp the gate does, on the same
 * backdrop, so the wait is already part of the place rather than a delay in
 * front of it.
 */
export class LoadingScreen {
  private readonly root: HTMLDivElement;
  private readonly title: HTMLParagraphElement;
  private readonly caption: HTMLParagraphElement;
  private readonly fill: HTMLDivElement;
  private readonly track: HTMLDivElement;
  private stage: LoadStage = "ui";
  private readonly unsubscribe: () => void;

  constructor(parent: HTMLElement) {
    this.title = el("p", { className: "zg-loading-title" });
    this.caption = el("p", { className: "zg-loading-caption" });
    this.fill = el("div", { className: "zg-loading-fill" });

    this.track = el("div", {
      className: "zg-loading-track",
      attrs: {
        role: "progressbar",
        "aria-valuemin": "0",
        "aria-valuemax": "100",
        "aria-valuenow": "0",
        "aria-label": "Loading progress",
      },
      children: [this.fill],
    });

    this.root = el("div", {
      className: "zg-loading is-visible",
      children: [
        vigilBackdrop(),
        el("div", {
          className: "zg-loading-inner",
          children: [diyaLamp(), this.title, this.track, this.caption],
        }),
      ],
    });

    parent.appendChild(this.root);
    this.markLoading(true);
    this.applyStrings();
    this.unsubscribe = onLocaleChange(() => this.applyStrings());
    this.set("ui");
  }

  private applyStrings(): void {
    this.title.textContent = t("loading.title");
    this.track.setAttribute("aria-label", t("loading.title"));
    this.caption.textContent = t(`loading.${this.stage}`);
  }

  set(stage: LoadStage): void {
    this.stage = stage;
    const percent = ((STAGES.indexOf(stage) + 1) / STAGES.length) * 100;
    this.fill.style.width = `${percent}%`;
    this.track.setAttribute("aria-valuenow", String(Math.round(percent)));
    this.caption.textContent = t(`loading.${stage}`);
  }

  /**
   * Marks the whole document while the curtain is up.
   *
   * The gate is built before the curtain comes down and sits underneath it,
   * embers and all, animating where nobody can see them. Those are composited
   * frames spent during the one stretch of the evening when the device is
   * already busy loading the engine, so they are parked until the curtain
   * lifts. Set on the root element because the two screens are siblings and
   * neither owns the other.
   */
  private markLoading(up: boolean): void {
    document.documentElement.classList.toggle("zg-is-loading", up);
  }

  /** Raise the curtain again — used for the wait after "Enter the venue". */
  show(): void {
    this.root.classList.add("is-visible");
    this.markLoading(true);
  }

  /**
   * Drop the curtain without tearing it down. The screen is reused between
   * "join card is ready" and "they pressed the button", so hiding must not
   * destroy it.
   */
  hide(): void {
    this.root.classList.remove("is-visible");
    this.markLoading(false);
  }

  /** Hide for good, once the venue can actually be walked around. */
  finish(): void {
    this.set("ready");
    this.hide();
    window.setTimeout(() => this.destroy(), 500);
  }

  destroy(): void {
    this.unsubscribe();
    this.markLoading(false);
    this.root.remove();
  }
}
